import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight, ArrowLeft, FolderOpen, Package } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';

const PanasonicCategoryPage = () => {
  const { worldSlug, categorySlug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [world, setWorld] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [worldSlug, categorySlug, lang]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch world info
      const worldRes = await api.get(`/panasonic/worlds/${worldSlug}`);
      setWorld(worldRes.data);

      if (categorySlug) {
        // Find category by slug
        const allCats = await api.get(`/panasonic/worlds/${worldSlug}/categories`);
        const cat = allCats.data.find(c => c.slug === categorySlug);
        
        if (cat) {
          // Fetch category details with breadcrumb
          const catRes = await api.get(`/panasonic/categories/${cat.id}`);
          setCurrentCategory(catRes.data);
          setBreadcrumb(catRes.data.breadcrumb || []);
          
          // If category has subcategories, show them; otherwise show products
          if (catRes.data.subcategories?.length > 0) {
            setCategories(catRes.data.subcategories);
          } else {
            setCategories([]);
          }
        }
      } else {
        // Fetch top-level categories for this world
        const catsRes = await api.get(`/panasonic/worlds/${worldSlug}/categories`);
        setCategories(catsRes.data);
        setCurrentCategory(null);
        setBreadcrumb([{
          name: worldRes.data.name,
          slug: worldRes.data.slug,
          is_world: true
        }]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    if (category.has_subcategories) {
      // Navigate to subcategory view
      navigate(`/panasonic/${worldSlug}/${category.slug}`);
    } else {
      // Navigate to products
      navigate(`/panasonic/${worldSlug}/${category.slug}/products`);
    }
  };

  const getBackLink = () => {
    if (!currentCategory) {
      return '/panasonic';
    }
    if (currentCategory.parent_category_id) {
      // Find parent slug from breadcrumb
      const parentBreadcrumb = breadcrumb[breadcrumb.length - 2];
      if (parentBreadcrumb && !parentBreadcrumb.is_world) {
        return `/panasonic/${worldSlug}/${parentBreadcrumb.slug}`;
      }
    }
    return `/panasonic/${worldSlug}`;
  };

  const getBackLabel = () => {
    if (!currentCategory) {
      return t('backToWorlds');
    }
    if (currentCategory.parent_category_id) {
      const parentBreadcrumb = breadcrumb[breadcrumb.length - 2];
      if (parentBreadcrumb) {
        return t('backTo', { name: parentBreadcrumb.name });
      }
    }
    return t('backTo', { name: world?.name || '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(getBackLink())}
        className="mb-4"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4 me-2" />
        {getBackLabel()}
      </Button>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap" data-testid="breadcrumb">
        <Link to="/panasonic" className="hover:text-primary">
          {t('productWorlds')}
        </Link>
        {breadcrumb.map((item, index) => (
          <React.Fragment key={item.id || index}>
            <ChevronRight className="h-4 w-4" />
            {index === breadcrumb.length - 1 ? (
              <span className="text-gray-900 font-medium">{item.name}</span>
            ) : (
              <Link 
                to={item.is_world ? `/panasonic/${item.slug}` : `/panasonic/${worldSlug}/${item.slug}`}
                className="hover:text-primary"
              >
                {item.name}
              </Link>
            )}
          </React.Fragment>
        ))}
      </nav>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-gray-500">Panasonic</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold mb-2">
          {currentCategory?.name || world?.name}
        </h1>
        <p className="text-gray-600">
          {currentCategory?.description || world?.description || world?.subtitle}
        </p>
      </div>

      {/* Categories Grid */}
      {categories.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => handleCategoryClick(category)}
              className="group bg-white border border-border rounded-lg overflow-hidden cursor-pointer hover:border-primary hover:shadow-lg transition-all duration-200"
              data-testid={`category-card-${category.slug}`}
            >
              {/* Category Image */}
              <div className="h-40 bg-gray-100 relative overflow-hidden">
                {category.image_url ? (
                  <img 
                    src={category.image_url} 
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                    <FolderOpen className="h-16 w-16 text-gray-300" />
                  </div>
                )}
                {/* Product Count Badge */}
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <Package className="h-3 w-3" />
                  {category.product_count || 0} {t('products')}
                </div>
              </div>

              {/* Category Info */}
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                  {category.name}
                </h3>
                {category.description && (
                  <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                    {category.description}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-primary text-sm font-medium">
                    {category.has_subcategories ? t('subcategories') : t('viewProducts')}
                  </span>
                  <ChevronRight className="h-4 w-4 text-primary group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : currentCategory && !currentCategory.subcategories?.length ? (
        // No categories - show link to products
        <div className="bg-white border border-border rounded-lg p-8 text-center">
          <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold mb-2">{t('productsInCategory')}</h3>
          <p className="text-gray-500 mb-4">
            {t('clickToViewProducts', { name: currentCategory.name })}
          </p>
          <Button onClick={() => navigate(`/panasonic/${worldSlug}/${categorySlug}/products`)} data-testid="show-products-btn">
            {t('showProducts')}
            <ChevronRight className="h-4 w-4 ms-2" />
          </Button>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{t('noCategories')}</p>
        </div>
      )}
    </div>
  );
};

export default PanasonicCategoryPage;
